import type { FocusEvent } from "react";

import { Progress } from "./Progress";
import { ShellIcon } from "./Shell";

// The sidebar footer usage row (docs/09 §4): a compact activity headline with
// a hover/focus detail card. Hosted billing arrives independently from activity.

export interface UsageRowCounter {
  id: string;
  label: string;
  quantity: number;
  // The formatted month-to-date value.
  value: string;
}

export interface UsageRowEntitlement {
  creditBalanceLabel: string;
  creditContext: string | null;
  meter: { max: number; value: number } | null;
  planName: string;
  warning: { message: string; role: "alert" | "status" } | null;
}

export interface UsageRowProps {
  counters: readonly UsageRowCounter[];
  // Muted error note. An error never renders as a zero: the row shows
  // "Unavailable" and the hover card shows this note instead of counters.
  errorNote?: string | null;
  entitlement?: UsageRowEntitlement | null;
  entitlementErrorNote?: string | null;
  // The formatted month-to-date headline for the compact row.
  headline?: string;
  isLoading?: boolean;
  isEntitlementLoading?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  // The direct link to /settings/usage (navigation is feature-owned so the
  // root scope params ride along).
  onOpenUsageSettings: () => void;
}

export function UsageRow({
  counters,
  errorNote = null,
  entitlement = null,
  entitlementErrorNote = null,
  headline,
  isLoading = false,
  isEntitlementLoading = false,
  isOpen,
  onOpenChange,
  onOpenUsageSettings,
}: UsageRowProps) {
  const isErrored = !isLoading && errorNote !== null;
  const isLoaded = !isLoading && errorNote === null;
  const detailsId = "marker-usage-row-details";

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      event.currentTarget.contains(nextTarget)
    ) {
      return;
    }
    onOpenChange(false);
  };

  return (
    <div
      className="marker-usage-row"
      onBlur={handleBlur}
      onFocus={() => {
        onOpenChange(true);
      }}
      onMouseEnter={() => {
        onOpenChange(true);
      }}
      onMouseLeave={() => {
        onOpenChange(false);
      }}
    >
      <div
        aria-describedby={isOpen ? detailsId : undefined}
        aria-label="Usage this month"
        className="marker-usage-row__summary"
      >
        <span className="marker-usage-row__label">Usage</span>
        {isLoading ? (
          <span aria-hidden="true" className="marker-usage-row__skeleton" />
        ) : null}
        {isErrored ? (
          <span className="marker-usage-row__note">Unavailable</span>
        ) : null}
        {isLoaded && headline ? (
          <span className="marker-usage-row__value">{headline}</span>
        ) : null}
        <button
          aria-label="Open usage settings"
          className="marker-usage-row__settings-link"
          onClick={() => {
            onOpenChange(false);
            onOpenUsageSettings();
          }}
          title="Usage settings"
          type="button"
        >
          <ShellIcon name="arrowUpRight" />
        </button>
      </div>
      {isOpen ? (
        <div
          aria-label="Usage this month details"
          className="marker-popover marker-usage-row__hovercard"
          id={detailsId}
        >
          <div className="marker-usage-row__heading">This month</div>
          {isLoading ? (
            <div
              aria-busy="true"
              aria-label="Loading usage"
              className="marker-usage-row__loading"
              role="status"
            >
              <span aria-hidden="true" className="marker-usage-row__skeleton" />
              <span aria-hidden="true" className="marker-usage-row__skeleton" />
              <span aria-hidden="true" className="marker-usage-row__skeleton" />
            </div>
          ) : null}
          {isErrored ? (
            <div className="marker-usage-row__note">{errorNote}</div>
          ) : null}
          {isLoaded
            ? counters.map((counter) => (
                <div className="marker-usage-row__counter" key={counter.id}>
                  <span className="marker-usage-row__counter-label">
                    {counter.label}
                  </span>
                  <span className="marker-usage-row__counter-value">
                    {counter.value}
                  </span>
                </div>
              ))
            : null}
          {isEntitlementLoading ? (
            <div
              aria-busy="true"
              aria-label="Loading billing details"
              className="marker-usage-row__billing"
              role="status"
            >
              <span aria-hidden="true" className="marker-usage-row__skeleton" />
            </div>
          ) : null}
          {entitlementErrorNote ? (
            <div className="marker-usage-row__billing">
              <div className="marker-usage-row__billing-heading">Billing</div>
              <div className="marker-usage-row__note">
                Billing details unavailable: {entitlementErrorNote}
              </div>
            </div>
          ) : null}
          {entitlement ? (
            <div className="marker-usage-row__billing">
              <div className="marker-usage-row__billing-heading">Billing</div>
              <div className="marker-usage-row__plan">
                {entitlement.planName}
              </div>
              {entitlement.warning ? (
                <div
                  className="marker-usage-row__warning"
                  role={entitlement.warning.role}
                >
                  {entitlement.warning.message}
                </div>
              ) : null}
              <div className="marker-usage-row__credit-value">
                {entitlement.creditBalanceLabel}
              </div>
              {entitlement.meter ? (
                <>
                  <Progress
                    className="marker-usage-row__meter"
                    label="Credit balance versus monthly allotment"
                    max={entitlement.meter.max}
                    value={entitlement.meter.value}
                  />
                  <div className="marker-usage-row__credit-context">
                    {entitlement.creditContext}
                  </div>
                </>
              ) : (
                <div className="marker-usage-row__credit-context">
                  {entitlement.creditContext}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
